import { useQuery } from "@tanstack/react-query";
import { getApiBaseUrl, impersonateHeaders } from "@/lib/api";
import type { WearableDeltasSection } from "./useWearableDeltas";

/**
 * Current wearable data for a user, anchored to today.
 * The "previous" comparison point is automatically the user's most recent lab visit.
 * Returns null when the user has no wearable data or the now-slice is too sparse.
 * The API answers only an impersonation token for this patient.
 *
 * Uses a raw fetch because the generated client is regenerated manually via
 * `./dev.sh openapi` — migrate to LabVisitsService once the spec is synced.
 */
export const useWearableCurrent = (
  token: string | null,
  authUserId: string | null | undefined,
) =>
  useQuery<WearableDeltasSection | null>({
    queryKey: ["wearable-current", authUserId],
    queryFn: async () => {
      const res = await fetch(
        `${getApiBaseUrl()}/api/admin/users/${authUserId}/wearable-current`,
        { headers: impersonateHeaders(token!) }
      );
      if (!res.ok) return null;
      const body = await res.json() as { section: WearableDeltasSection | null };
      return body.section;
    },
    enabled: !!token && !!authUserId,
    staleTime: 60_000,
  });
